import { Card, List, MenuItem, Popper } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { MutableRefObject } from 'react';
import { siteMeilisearchSearchKeyKeys } from 'src/api/query-keys/site/meilisearch-search-key';
import { useAvailableSites } from 'src/hooks/api/use-available-sites';
import { useUserStore } from 'src/store/user-store';
import { UserRole } from 'src/types/enum/user-role';

interface Props {
  anchorRef: MutableRefObject<HTMLElement | null>;
  open: boolean;
  onClose: () => void;
  onMouseLeave?: () => void;
  onMouseEnter?: () => void;
  placement: 'bottom-end' | 'right-start';
}

export const AvailableSitesPopper = ({
  anchorRef,
  open,
  onClose,
  onMouseLeave,
  onMouseEnter,
  placement,
}: Props) => {
  const setViewContext = useUserStore(state => state.setViewContext);
  const setIsChangingDirectory = useUserStore(
    state => state.setIsChangingDirectory,
  );
  const setDirectoryToken = useUserStore(state => state.setDirectoryToken);
  const setAdminToken = useUserStore(state => state.setAdminToken);
  const setRole = useUserStore(state => state.setRole);

  const queryClient = useQueryClient();

  const navigate = useNavigate();

  const { data: availableSites } = useAvailableSites();

  const handleChangeContext = (
    directoryToken: string,
    adminToken: string,
    role: UserRole,
  ) => {
    setViewContext('site');
    setIsChangingDirectory(true);
    setDirectoryToken(directoryToken);
    setAdminToken(adminToken);
    setRole(role);

    onClose();

    queryClient.invalidateQueries({
      queryKey: siteMeilisearchSearchKeyKeys.details(),
    });

    navigate({
      to: '/',
    });
  };

  return (
    <Popper
      anchorEl={anchorRef.current}
      open={open}
      placement={placement}
      onMouseLeave={onMouseLeave}
      onMouseEnter={onMouseEnter}
      sx={{
        margin: placement === 'bottom-end' ? '10px 0 !important' : undefined,
        top: placement === 'right-start' ? '-10px !important' : undefined,
      }}>
      <Card variant="outlined">
        <List>
          {availableSites?.resources?.map(site => (
            <MenuItem
              key={site.token}
              onClick={() => {
                handleChangeContext(site.token, site.siteAdminToken, site.role);
              }}>
              {site.name}
            </MenuItem>
          ))}
        </List>
      </Card>
    </Popper>
  );
};
